/**
 * Inline validation for the theme's native Shopify forms (contact, newsletter).
 *
 * The browser's own constraint validation still decides what is valid and still
 * blocks the submit - this only replaces its tooltip with a message under the
 * field. Handling the `invalid` event rather than setting novalidate and
 * checking on submit matters: an invalid form never fires `submit`, so
 * tracking.js keeps treating it as unsubmitted and its form_fallout still fires.
 *
 * Opt a form in with data-validate. Each field names its error element with
 * aria-describedby (snippets/form-field-error.liquid) and can carry its own
 * wording in data-error-required / data-error-invalid; anything without falls
 * back to the browser's message.
 */
const FORM = "form[data-validate]";
const focusQueued = new WeakSet();

function errorElementFor(field) {
  const ids = (field.getAttribute("aria-describedby") || "").split(/\s+/);
  for (const id of ids) {
    const el = id && document.getElementById(id);
    if (el?.hasAttribute("data-field-error")) return el;
  }
  return null;
}

function messageFor(field) {
  if (field.validity.valueMissing) {
    return field.dataset.errorRequired || field.validationMessage;
  }
  return field.dataset.errorInvalid || field.validationMessage;
}

function showError(field) {
  field.setAttribute("aria-invalid", "true");
  const el = errorElementFor(field);
  if (!el) return;
  el.textContent = messageFor(field);
  el.hidden = false;
}

function clearError(field) {
  field.removeAttribute("aria-invalid");
  const el = errorElementFor(field);
  if (!el) return;
  el.textContent = "";
  el.hidden = true;
}

// `invalid` does not bubble, so it is caught on the way down.
document.addEventListener(
  "invalid",
  (event) => {
    const field = event.target;
    if (!field.form?.matches(FORM)) return;

    event.preventDefault();
    showError(field);

    // Every invalid field fires in one synchronous pass, so focus waits for
    // the pass to finish and then goes to the first one flagged.
    const { form } = field;
    if (focusQueued.has(form)) return;
    focusQueued.add(form);
    queueMicrotask(() => {
      focusQueued.delete(form);
      form.querySelector('[aria-invalid="true"]')?.focus();
    });
  },
  true,
);

// A flagged field clears as soon as it is fixed, and its message follows what
// is typed (empty -> required, half an email -> invalid) until then. Fields not
// yet flagged are left alone so nobody is told off mid-word.
function onEdit(event) {
  const field = event.target;
  if (!field.form?.matches(FORM) || field.getAttribute("aria-invalid") !== "true") return;

  if (field.validity.valid) {
    clearError(field);
  } else {
    showError(field);
  }
}

document.addEventListener("input", onEdit, true);
document.addEventListener("change", onEdit, true);
